/**
 * Google reCAPTCHA v3 client glue. Everything here is safe when the
 * feature is unconfigured (no recaptchaSiteKey in etmConfig): every
 * export resolves to null / no-ops and Google's script is never
 * requested — the unconfigured app keeps its zero-external-requests
 * property.
 *
 * When configured, the script is injected dynamically the first time
 * it's needed (AuthScreen mount), never via a static tag: logged-in
 * users — the overwhelming majority of PWA page loads — never contact
 * Google at all. Tokens are minted fresh per submit (they expire after
 * 2 minutes; caching one would break for anyone who sits on the login
 * form). Every failure path resolves null rather than throwing: the
 * backend is the enforcement point and produces the user-facing error,
 * the frontend only decorates it (see recaptchaScriptFailed()).
 */

const SCRIPT_TIMEOUT_MS = 8000;

let loadPromise = null;
let scriptFailed = false;

export function recaptchaSiteKey() {
  return (window.etmConfig || {}).recaptchaSiteKey || '';
}

/**
 * True when we know the Google script could not be loaded (adblocker,
 * offline, …) — lets AuthScreen show a message naming the actual
 * problem instead of the server's generic rejection.
 */
export function recaptchaScriptFailed() {
  return scriptFailed;
}

function loadScript() {
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = new Promise((resolve, reject) => {
    // Already present (another shell loaded it, or a test stubbed it).
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(recaptchaSiteKey());
    script.async = true;
    script.defer = true;
    // An adblocker that blackholes the request (never load, never error)
    // must not hang the submit forever.
    const timer = setTimeout(() => reject(new Error('recaptcha script timeout')), SCRIPT_TIMEOUT_MS);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error('recaptcha script blocked'));
    };
    document.head.appendChild(script);
  });
  loadPromise.catch(() => {
    scriptFailed = true;
    loadPromise = null; // allow a retry on the next submit
  });
  return loadPromise;
}

/**
 * Fire-and-forget warm-up so the first submit isn't slowed by the
 * script download. Called on AuthScreen mount.
 */
export function preloadRecaptcha() {
  if (!recaptchaSiteKey()) {
    return;
  }
  loadScript().catch(() => {});
}

/**
 * @returns {Promise<string|null>} A fresh v3 token for the given action
 *   ('register'/'login'), or null on ANY failure — never throws.
 */
export async function getRecaptchaToken(action) {
  const siteKey = recaptchaSiteKey();
  if (!siteKey) {
    return null;
  }
  try {
    await loadScript();
    await new Promise((resolve) => window.grecaptcha.ready(resolve));
    const token = await window.grecaptcha.execute(siteKey, { action });
    scriptFailed = false;
    return typeof token === 'string' && token !== '' ? token : null;
  } catch {
    return null;
  }
}
